import { fromNode } from '@rehaje/core'
import { reactive, toJS } from '@rehaje/reactive'
import { Path, toArr } from '@rehaje/shared'

import {
  FormBlurEvent,
  FormFocusEvent,
  FormInitialValueChangeEvent,
  FormInputEvent,
  FormResetEvent,
  FormSubmitErrorEvent,
  FormSubmitEvent,
  FormSubmitSuccessEvent,
  FormValidateEvent,
  FormValueChangeEvent
} from '../events'
import {
  getEventValues,
  isHTMLInputEvent,
  isHTMLInputEventFromSelf
} from '../utils'
import { SchemaModel } from './SchemaModel'

import type { SchemaMessages } from '../types'
import type { ValuableNode } from './ValuableNode'
import type { PathLikes } from '@rehaje/shared'

export class ValuableModel<
  ValueType = any,
  Node extends ValuableNode = ValuableNode
> extends SchemaModel<Node> {
  /**
   * 缓存现场值
   */
  __r_cache_value?: any

  /**
   * 表单初始值
   */
  @reactive('deep')
  __r_form_initial_values?: any

  /**
   * 表单值
   */
  @reactive('deep')
  __r_form_values?: any

  /**
   * 表单或字段是否被修改过
   */
  @reactive()
  __r_modified?: boolean

  /**
   * 属性同步初始值
   */
  @reactive()
  __r_self_initial_value?: any

  __r_submit_promise?: Promise<any>

  /**
   * 是否提交中
   */
  @reactive('debounce')
  __r_submitting?: boolean

  @reactive()
  __r_validate_errors: SchemaMessages[] = []

  @reactive()
  __r_validated = false

  /**
   * 激活态
   */
  @reactive()
  active: boolean = false

  /**
   * 输入事件值
   */
  @reactive()
  inputValue?: any

  /**
   * 输入事件参数元组
   */
  @reactive()
  inputValues?: any[]

  @reactive()
  validated: boolean = false

  /**
   * 是否已访问
   */
  @reactive()
  visited: boolean = false

  get cacheValue() {
    return toJS(this.value ?? this.initialValue ?? this.__r_cache_value)
  }

  /**
   * @deprecated
   */
  get feedback() {
    return {
      clearErrors: (..._args: any[]) => {
        this.clearErrors()
      },
      clearFeedbacks: (..._args: any[]) => {
        this.clearFeedbacks()
      },
      setErrors: (message: string, _type?: string) => {
        this.errors = [message]
      }
    }
  }

  override get form() {
    return this.getForm()
  }

  /**
   * 表单或字段初始值
   */
  @fromNode()
  get initialValue(): ValueType {
    return this.__r_self_initial_value
  }

  set initialValue(initialValue: ValueType) {
    this.setInitialValue(initialValue)
  }

  @reactive('computed')
  get isOverflow() {
    const index = Number(this.index)
    if (index === -1) return false
    const parents = this.valuableParents
    const parent = parents.find((parent) => parent.type === 'Schema.Array')
      ?.value as any[]
    if (parent === undefined) return true
    return parent.length - 1 < index
  }

  /**
   * 是否已修改
   */
  @reactive('computed')
  get modified(): boolean {
    return (
      this.__r_modified || this.valuableChildren.some((child) => child.modified)
    )
  }

  /**
   * 字段路径
   */
  @reactive('computed')
  override get path() {
    return new Path(this.valuePath)
  }

  get submitting() {
    return this.__r_submitting ?? false
  }

  set submitting(submitting: boolean) {
    this.__r_submitting = submitting
  }

  get valuable() {
    if (this.hidden && this.removeValueOnHidden) return false
    return true
  }

  /**
   * 表单或字段值
   */
  @reactive('computed')
  get value(): ValueType {
    if (this.is(this.form)) return this.__r_form_values
    return this.path.getIn(this.form.__r_form_values)
  }

  set value(value: ValueType) {
    this.setValue(value)
  }

  constructor(node: ValuableNode) {
    super(node)
    if (node.value === undefined) {
      this.addEffect(() => {
        const _value = toJS(this.initialValue)
        if (_value !== undefined && this.value === undefined) {
          this.setValue(_value)
        }
      })
      this.addReaction(
        () => this.initialValue,
        (value) => {
          if (this.valuable) this.setValue(toJS(value))
        }
      )
    } else {
      this.setValue(node.value)
    }

    this.addReaction(
      () => this.value,
      (value, oldValue) => {
        this.dispatchEvent(() => {
          this.dispatchEvent(new FormValueChangeEvent({ oldValue, value }))
          if (this.__r_modified) {
            this.validateSelf()
          }
        })
      }
    )

    this.addReaction(
      () => this.value,
      (value) => {
        if (!this.valuable && value !== undefined) {
          this.clearValue()
        }
      },
      {
        fireImmediately: true
      }
    )

    this.addReaction(
      () => this.initialValue,
      () => this.dispatchEvent(new FormInitialValueChangeEvent())
    )
    this.addReaction(
      () => this.interactionStatus,
      (state) => {
        if (state !== 'editable' && state !== 'writeOnly') {
          this.clearErrors()
        }
      }
    )
  }

  async __r_submit<T = any>(onSubmit?: (values: ValueType) => T): Promise<T> {
    this.validate(true)
    if (this.errors.length === 0) {
      let payload = toJS(this.value)
      if (onSubmit) {
        payload = (await onSubmit?.(payload)) as ValueType
      }
      this.dispatchEvent(new FormSubmitSuccessEvent(payload))
      return Promise.resolve(payload) as Promise<T>
    } else {
      this.invalidFields[0]?.scrollIntoView()
      const feedbacks = this.feedbacks
      this.dispatchEvent(new FormSubmitErrorEvent(feedbacks))
      return Promise.reject(feedbacks) as Promise<T>
    }
  }

  clearCacheValue() {
    this.__r_cache_value = undefined
  }

  /**
   * 清理错误信息
   */
  clearErrors() {
    this.__r_validate_errors = []
    this.__r_errors = undefined
  }

  /**
   * 清理所有提示信息
   */
  clearFeedbacks() {
    this.clearErrors()
    this.clearSuccesses()
    this.clearWarnings()
  }

  /**
   * @deprecated
   */
  clearSpare() {
    this.clearCacheValue()
  }

  /**
   * 清理成功信息
   */
  clearSuccesses() {
    this.__r_successes = undefined
  }

  /**
   * 清理表单或字段值
   */
  clearValue() {
    if (this.recoverValueOnShown) {
      this.__r_cache_value ??= toJS(this.value ?? this.initialValue)
    }
    if (this.is(this.form)) {
      this.setValue(undefined)
    } else {
      this.form.deleteValueIn(this.path.parts)
    }
  }

  /**
   * 清理警告信息
   */
  clearWarnings() {
    this.__r_warnings = undefined
  }

  /**
   * @deprecated
   * @param pattern
   */
  deleteValueIn(pattern?: PathLikes) {
    Path.deleteIn(this.form.__r_form_values, pattern)
  }

  override getControlErrors() {
    if (this.hidden) return []
    if (this.modified || this.validated)
      return toArr(this.__r_errors).filter(Boolean)
    return []
  }

  override getControlSuccesses() {
    if (this.modified || this.validated)
      return toArr(this.__r_successes).filter(Boolean)
    return []
  }

  override getControlWarnings() {
    if (this.modified || this.validated)
      return toArr(this.__r_warnings).filter(Boolean)
    return []
  }

  override getForm(): ValuableModel {
    return super.getForm() ?? this
  }

  override getSelfErrors() {
    return [
      ...this.getControlErrors(),
      ...this.__r_validate_errors.flat()
    ].filter(Boolean)
  }

  override getSelfSuccesses() {
    return [
      ...this.getControlSuccesses(),
      ...this.__r_validate_errors.flat()
    ].filter(Boolean)
  }

  override getSelfWarnings() {
    return [
      ...this.getControlWarnings(),
      ...this.__r_validate_errors.flat()
    ].filter(Boolean)
  }

  override getSuccesses() {
    if (this.hidden) return []
    return super.getSuccesses()
  }

  override getWarnings() {
    if (this.hidden) return []
    return super.getWarnings()
  }

  /**
   * 失焦触发器
   * @param event
   */
  onBlur = async (event?: any) => {
    if (event?.target && !isHTMLInputEvent(event, false)) return
    this.active = false
    this.dispatchEvent(new FormBlurEvent(event))
  }

  /**
   * 焦点触发器
   * @param event
   */
  onFocus = (event?: any) => {
    if (event?.target && !isHTMLInputEvent(event, false)) return
    this.active = true
    this.visited = true
    this.dispatchEvent(new FormFocusEvent(event))
  }

  /**
   * 输入触发器
   * @param event
   * @param args
   */
  onInput = (event?: any, ...args: any[]) => {
    if (!isHTMLInputEventFromSelf(event)) return
    this.dispatchEvent(() => {
      const values = getEventValues(event, args)
      this.inputValue = values[0]
      this.inputValues = values
      this.__r_modified = true
      this.setValue(values[0])
      this.dispatchEvent(new FormInputEvent(values, event))
    })
  }

  /**
   * 恢复表单或字段值
   */
  recoverValue() {
    if (!this.recoverValueOnShown) return
    const value = this.cacheValue
    if (value !== undefined && this.value === undefined) {
      this.setValue(value)
      this.__r_cache_value = undefined
    }
  }

  /**
   * 重置表单或字段(包含子节点)
   * @param pattern
   */
  @reactive('action')
  reset(pattern?: PathLikes) {
    if (pattern) {
      this.root
        .findAll<ValuableModel>((model) => {
          if (model instanceof ValuableModel && model.path.match(pattern))
            return true
          return false
        })
        .forEach((child) => child.resetSelf())
    } else {
      this.resetSelf()
      this.valuableDescendants.forEach((child) => child.resetSelf())
    }
  }

  /**
   * 重置自身
   */
  resetSelf() {
    this.dispatchEvent(() => {
      this.dispatchEvent(new FormResetEvent())
      this.__r_validated = false
      this.__r_modified = false
      this.active = false
      this.visited = false
      this.inputValue = undefined
      this.inputValues = []
      this.setValue(toJS(this.initialValue))
      this.clearFeedbacks()
    })
  }

  /**
   * 设置表单或字段初始值
   * @param initialValue
   */
  setInitialValue(initialValue: any) {
    this.__r_self_initial_value = initialValue
  }

  /**
   * 设置表单或字段值
   * @param value
   */
  @reactive('action')
  setValue(value: any) {
    if (!this.valuable && value !== undefined) return
    const _value = this.transform(value)
    if (this.is(this.form)) {
      this.__r_form_values = _value
      return
    }
    if (this.isOverflow) return
    this.form.setInProperty(['__r_form_values', ...this.path.parts], _value)
  }

  /**
   * @deprecated
   * @param pattern
   * @param value
   */
  setValueIn(pattern?: PathLikes, value?: any) {
    Path.setIn(this.form.__r_form_values, pattern, value)
  }

  /**
   * 表单提交方法
   * @param onSubmit
   */
  async submit<T = any>(onSubmit?: (values: ValueType) => T): Promise<T> {
    if (this.submitting || this.__r_submit_promise)
      return this.__r_submit_promise as any
    this.submitting = true
    try {
      this.__r_submit_promise = new Promise((resolve, reject) => {
        this.dispatchEvent(() => {
          this.dispatchEvent(new FormSubmitEvent())
          this.__r_submit(onSubmit)
            .then(resolve)
            .catch(reject)
            .finally(() => (this.__r_submit_promise = undefined))
        })
      })
      return (await this.__r_submit_promise) as Promise<any>
    } finally {
      this.submitting = false
    }
  }

  /**
   * 类型转换方法
   * todo: 需要考虑字段裁剪
   * @param value
   * @returns
   */
  transform(value: any) {
    if (this.store.options?.disableTypeTransformer) return value
    const defStore = this.store.definition
    return defStore.transformWithSchema(value, this.node as any)
  }

  /**
   * 主动校验方法
   */
  validate(strict = false) {
    this.validateSelf(strict)
    this.valuableChildren.forEach((child) => child.validate(strict))
  }

  /**
   * 校验自身
   */
  validateSelf(strict = false) {
    const defStore = this.store.definition
    const errors = defStore.validateWithSchema(
      this.value,
      this as any,
      false,
      true,
      strict
    )
    if (typeof this.validator === 'function') {
      const customError = this.validator(this.value, this.form.__r_form_values)
      if (typeof customError === 'string') {
        errors.push(new Error(customError))
      }
    }
    this.__r_validate_errors = errors.map((error) => error.message)
    this.__r_validated = true
    this.validated = true
    this.dispatchEvent(new FormValidateEvent(this.__r_validate_errors))
  }
}
ValuableModel.prototype.__r_is_valuable_model = true
